/**
 * <contact-form> Web Component
 *
 * Client-side form validation for Shopify contact forms.
 * Validates required fields and email format before submission.
 * Displays inline error messages in .form-field__error spans.
 *
 * Each input snippet wrapper must have a [data-form-field] attribute
 * and contain a .form-field__error span for error messages.
 */
class ContactForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.form.novalidate = true;
    this.form.addEventListener('submit', this.onSubmit.bind(this));

    // Live validation on blur and clear on input
    this.form
      .querySelectorAll(
        '[data-form-field] input, [data-form-field] textarea, [data-form-field] select',
      )
      .forEach((field) => {
        field.addEventListener('blur', () => this.validateField(field));
        field.addEventListener('input', () => {
          const wrapper = field.closest('[data-form-field]');
          if (wrapper && wrapper.classList.contains('form-field--invalid')) {
            this.validateField(field);
          }
        });
        field.addEventListener('change', () => {
          const wrapper = field.closest('[data-form-field]');
          if (wrapper && wrapper.classList.contains('form-field--invalid')) {
            this.validateField(field);
          }
        });
      });
  }

  onSubmit(event) {
    this.clearErrors();

    const fields = this.form.querySelectorAll(
      '[data-form-field] input, [data-form-field] textarea, [data-form-field] select',
    );
    let isValid = true;

    fields.forEach((field) => {
      if (!this.validateField(field)) {
        isValid = false;
      }
    });

    if (!isValid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const firstInvalid = this.form.querySelector('.form-field--invalid');
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = firstInvalid.querySelector('input, textarea, select');
        if (focusable) focusable.focus();
      }
    }
  }

  validateField(field) {
    // Skip Shopify hidden fields (form_type, authenticity_token, etc.)
    if (field.type === 'hidden') return true;

    const wrapper = field.closest('[data-form-field]');
    if (!wrapper) return true;

    const errorEl = wrapper.querySelector('.form-field__error');
    let message = '';

    // Required check
    if (field.required) {
      if (field.type === 'radio') {
        // Only show error on the last radio of the group
        const radios = this.form.querySelectorAll(
          `[data-form-field] input[name="${field.name}"]`,
        );
        if (radios.length > 0 && field !== radios[radios.length - 1]) {
          return true;
        }
        const anyChecked = Array.from(radios).some((r) => r.checked);
        if (!anyChecked) {
          message = 'Please select an option';
        }
      } else if (field.type === 'checkbox') {
        // For checkbox groups with same name, only validate on the last one
        const checkboxes = this.form.querySelectorAll(
          `[data-form-field] input[type="checkbox"][name="${field.name}"]`,
        );
        if (
          checkboxes.length > 1 &&
          field !== checkboxes[checkboxes.length - 1]
        ) {
          return true;
        }
        // Check if at least one checkbox in the group is checked
        const anyChecked = Array.from(checkboxes).some((cb) => cb.checked);
        if (!anyChecked) {
          message = 'This field is required';
        }
      } else if (field.value.trim() === '') {
        message = 'This field is required';
      }
    }

    // Email validation
    if (!message && field.type === 'email' && field.value.trim() !== '') {
      if (!this.EMAIL_REGEX.test(field.value.trim())) {
        message = 'Please enter a valid email address';
      }
    }

    // Min length validation
    if (
      !message &&
      field.minLength > 0 &&
      field.value.length > 0 &&
      field.value.length < field.minLength
    ) {
      message = `Minimum ${field.minLength} characters required`;
    }

    // Max length validation
    if (
      !message &&
      field.maxLength > 0 &&
      field.maxLength < 100000 &&
      field.value.length > field.maxLength
    ) {
      message = `Maximum ${field.maxLength} characters allowed`;
    }

    // Number validations
    if (!message && field.type === 'number' && field.value !== '') {
      const val = parseFloat(field.value);
      if (field.min !== '' && val < parseFloat(field.min)) {
        message = `Value must be at least ${field.min}`;
      }
      if (field.max !== '' && val > parseFloat(field.max)) {
        message = `Value must be at most ${field.max}`;
      }
    }

    if (message) {
      this.showError(wrapper, errorEl, message, field);
      return false;
    }

    this.clearError(wrapper, errorEl, field);
    return true;
  }

  showError(wrapper, errorEl, message, field) {
    wrapper.classList.add('form-field--invalid');

    if (field.classList.contains('form-field__input')) {
      field.style.borderColor = 'var(--color-error)';
    }

    // Also highlight the custom select trigger if this is a select field
    const selectTrigger = wrapper.querySelector('.input-select__trigger');
    if (selectTrigger) {
      selectTrigger.style.borderColor = 'var(--color-error)';
    }

    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    }
  }

  clearError(wrapper, errorEl, field) {
    wrapper.classList.remove('form-field--invalid');

    if (field.classList.contains('form-field__input')) {
      field.style.borderColor = '';
    }

    // Also clear the custom select trigger border
    const selectTrigger = wrapper.querySelector('.input-select__trigger');
    if (selectTrigger) {
      selectTrigger.style.borderColor = '';
    }

    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.add('hidden');
    }
  }

  clearErrors() {
    this.querySelectorAll('.form-field--invalid').forEach((el) => {
      el.classList.remove('form-field--invalid');
    });

    this.querySelectorAll('.form-field__error').forEach((el) => {
      el.textContent = '';
      el.classList.add('hidden');
    });

    this.querySelectorAll('.form-field__input').forEach((el) => {
      el.style.borderColor = '';
    });

    this.querySelectorAll('.input-select__trigger').forEach((el) => {
      el.style.borderColor = '';
    });
  }
}

if (!customElements.get('contact-form')) {
  customElements.define('contact-form', ContactForm);
}
